// Okunmamış sayacının uygulama genelindeki tek kaynağı (mimari 9.26.7.5).
//
// Dock her ekranda görünüyor ama okunmamış veriyi çeken tek yer InboxScreen'di
// ve ekranlar koşullu render edildiği için oradan çıkıldığında veri de gidiyordu.
// Sayaç bu yüzden ekranların üstünde duruyor. ThemeContext/I18nContext ile aynı
// desen; farkı, bunun yapılandırma değil **veri** taşıması — yükleme, hata ve
// tazeleme durumları da burada.
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AppState, AppStateStatus } from 'react-native';
import { api, Conversation } from '../services/api';
import { totalUnread, pendingMeetupCount, conversationUnread } from './badge';
import { applyWebBadge } from './webBadge';

type UnreadValue = {
  /** Toplam okunmamış mesaj — susturulmuş, arşivlenmiş ve engellenmiş hariç. */
  messages: number;
  /** Cevap bekleyen buluşma teklifi sayısı (Takvim sekmesi rozeti). */
  meetups: number;
  /** Bildirim Merkezi'ndeki okunmamış kanonik olay sayısı (zil rozeti). */
  notifications: number;
  /** Sunucudan yeniden oku. */
  refresh: () => void;
  /** Gelen kutusu listesini zaten çekmişken sayacı ondan besle (ikinci istek atma). */
  syncFromConversations: (list: Conversation[]) => void;
  /** Sohbet açıldığında sayacı beklemeden düşür (iyimser güncelleme). */
  markConversationRead: (otherId: number) => void;
};

const UnreadContext = createContext<UnreadValue>({
  messages: 0,
  meetups: 0,
  notifications: 0,
  refresh: () => {},
  syncFromConversations: () => {},
  markConversationRead: () => {},
});

// Uygulama ön plandayken sayacı tazeleme aralığı. Push gelene kadar (9.26.2)
// artışı öğrenmenin tek yolu sormak; okuma tarafı zaten iyimser düşüyor.
// Sohbet ekranının 5 sn'lik yoklamasından bilerek çok daha seyrek.
const POLL_MS = 60000;

export function UnreadProvider({ token, children }: { token: string; children: React.ReactNode }) {
  const [messages, setMessages] = useState(0);
  const [meetups, setMeetups] = useState(0);
  const [notifications, setNotifications] = useState(0);
  // Son bilinen sohbet-başı sayılar: iyimser düşürme buradan hesaplanıyor.
  const perConversation = useRef<Map<number, number>>(new Map());

  const applyConversations = useCallback((list: Conversation[]) => {
    const map = new Map<number, number>();
    for (const c of list) map.set(c.other_id, c.muted ? 0 : conversationUnread(c));
    perConversation.current = map;
    setMessages(totalUnread(list));
  }, []);

  const refresh = useCallback(() => {
    if (!token) {
      perConversation.current = new Map();
      setMessages(0);
      setMeetups(0);
      setNotifications(0);
      return;
    }
    // Hata durumunda son bilinen sayı korunuyor (9.26.7.4.3): rozetin kaybolması
    // kullanıcıya "okunmamışın yok" demek olurdu — sessizce yanlış bir sıfır.
    api.getConversations(token).then(applyConversations).catch(() => {});
    api.getMeetups(token, 'pending')
      .then((list) => setMeetups(pendingMeetupCount(list)))
      .catch(() => {});
    api.getNotificationUnreadCount(token)
      .then(({ count }) => setNotifications(count))
      .catch(() => {});
  }, [token, applyConversations]);

  const markConversationRead = useCallback((otherId: number) => {
    const pending = perConversation.current.get(otherId) ?? 0;
    if (pending <= 0) return;
    perConversation.current.set(otherId, 0);
    setMessages((n) => Math.max(0, n - pending));
  }, []);

  // Web sekmesi rozeti (9.26.7.2.3): sekme arka plandayken dock görünmüyor,
  // başlık ve favicon o boşluğu dolduruyor. Native'de sessizce hiçbir şey yapmaz.
  useEffect(() => { applyWebBadge(messages); }, [messages]);

  // Tetikleyiciler (9.26.7.4.1): oturum açılışı + ön plana dönüş + seyrek yoklama.
  useEffect(() => {
    refresh();
    if (!token) return;

    let timer = setInterval(refresh, POLL_MS);
    const onAppState = (state: AppStateStatus) => {
      // Arka planda yoklama boşa istek; dönüşte sayaç bayat olduğu için hemen tazele.
      clearInterval(timer);
      if (state === 'active') {
        refresh();
        timer = setInterval(refresh, POLL_MS);
      }
    };
    const sub = AppState.addEventListener('change', onAppState);
    return () => {
      clearInterval(timer);
      sub.remove();
    };
  }, [token, refresh]);

  return (
    <UnreadContext.Provider
      value={{ messages, meetups, notifications, refresh, syncFromConversations: applyConversations, markConversationRead }}>
      {children}
    </UnreadContext.Provider>
  );
}

export function useUnread(): UnreadValue {
  return useContext(UnreadContext);
}
